/**
 * Security Hardening Utilities
 * Hides technical details, prevents inspection, and minimizes information disclosure
 */

/**
 * Remove technical details from page title
 * Changes: "React App | Login | Dog Grooming" → "Dog Grooming"
 */
export const sanitizePageTitle = (businessName = 'Dog Grooming System') => {
  // Remove framework names, technical terms
  document.title = businessName;
  
  // Remove meta tags that reveal technology
  const metaTags = document.querySelectorAll('meta[name*="react"], meta[name*="webpack"], meta[name*="generator"]');
  metaTags.forEach(tag => tag.remove());
};

/**
 * Hide framework and library information
 */
export const hideFrameworkInfo = () => {
  // Remove React DevTools detection
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
      isDisabled: true,
      supportsFiber: true,
      inject: () => {},
      onCommitFiberRoot: () => {},
      onCommitFiberUnmount: () => {},
    };
  }

  // Remove Redux DevTools
  if (window.__REDUX_DEVTOOLS_EXTENSION__) {
    delete window.__REDUX_DEVTOOLS_EXTENSION__;
  }

  // Hide React root
  const reactRoots = document.querySelectorAll('[data-reactroot], [data-reactid]');
  reactRoots.forEach(root => {
    root.removeAttribute('data-reactroot');
    root.removeAttribute('data-reactid');
  });
};

/**
 * Disable console in production
 * Prevents attackers from using console for debugging
 */
export const disableConsole = () => {
  // Only disable in production, allow in development
  if (process.env.NODE_ENV === 'production') {
    // Save original console methods
    const noop = () => {};
    
    // Override console methods
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
    // Keep console.error for critical errors (but sanitize messages)
    const originalError = console.error;
    console.error = (...args) => {
      // Log generic message instead of detailed error
      originalError('An error occurred');
    };
  }
  // In development, console works normally
};

/**
 * Prevent DevTools detection and usage
 */
export const preventDevTools = () => {
  // Skip in development mode
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  // Skip inside iframes — outer/inner size comparison is always a false positive there
  if (window.self !== window.top) {
    return;
  }

  // Detect DevTools
  const detectDevTools = () => {
    const threshold = 160;
    const widthThreshold = window.outerWidth - window.innerWidth > threshold;
    const heightThreshold = window.outerHeight - window.innerHeight > threshold;
    return widthThreshold || heightThreshold;
  };

  // Check periodically
  setInterval(() => {
    if (detectDevTools()) {
      // Blur entire page
      document.body.style.filter = 'blur(5px)';
      document.body.style.pointerEvents = 'none';
      
      // Optional: Redirect or show message
      // window.location.href = '/';
    } else {
      document.body.style.filter = '';
      document.body.style.pointerEvents = '';
    }
  }, 1000);

  // Prevent common DevTools shortcuts (only in production)
  if (process.env.NODE_ENV === 'production') {
    document.addEventListener('keydown', (e) => {
      // F12
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+J
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+C
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        return false;
      }
      // Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        return false;
      }
    });
  }
};

/**
 * Remove HTML comments that might reveal information
 */
export const removeHTMLComments = () => {
  const removeComments = (node) => {
    const iterator = document.createNodeIterator(
      node,
      NodeFilter.SHOW_COMMENT,
      null
    );
    
    const comments = [];
    let currentNode;
    // eslint-disable-next-line no-cond-assign
    while ((currentNode = iterator.nextNode())) {
      comments.push(currentNode);
    }
    
    comments.forEach(comment => comment.remove());
  };

  removeComments(document.body);
  
  // Remove on DOM changes
  const observer = new MutationObserver(() => {
    removeComments(document.body);
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};

/**
 * Obfuscate class names and IDs
 * Makes it harder to target elements
 */
export const obfuscateClassNames = () => {
  // This would need to be done at build time with webpack/babel
  // For runtime, we can add random attributes
  const elements = document.querySelectorAll('[class], [id]');
  elements.forEach(el => {
    // Add random data attribute to confuse attackers
    el.setAttribute('data-x', Math.random().toString(36).substring(7));
  });
};

/**
 * Hide source maps in production
 */
export const hideSourceMaps = () => {
  // Remove source map references
  const scripts = document.querySelectorAll('script');
  scripts.forEach(script => {
    if (script.src && script.src.includes('.map')) {
      script.remove();
    }
  });
};

/**
 * Prevent right-click globally (only in production)
 */
export const disableRightClickGlobally = () => {
  if (process.env.NODE_ENV === 'production') {
    document.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      return false;
    });
  }
};

/**
 * Prevent frame injection (clickjacking)
 */
export const preventFrameInjection = () => {
  // Only break out of cross-origin iframes (clickjacking risk).
  // Same-origin iframes (e.g. the demo page at /try.html) are safe and allowed.
  if (window.self !== window.top) {
    try {
      const parentOrigin = window.top.location.origin;
      const selfOrigin = window.self.location.origin;
      if (parentOrigin !== selfOrigin) {
        window.top.location = window.self.location;
      }
    } catch (e) {
      // If we can't read top.location it's cross-origin — break out
      window.top.location = window.self.location;
    }
  }
};

/**
 * Add security headers via meta tags
 */
export const addSecurityHeaders = () => {
  // Skip CSP in development (it blocks localhost API calls)
  if (process.env.NODE_ENV === 'production') {
    // Content Security Policy
    const csp = document.createElement('meta');
    csp.httpEquiv = 'Content-Security-Policy';
    csp.content = "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'; connect-src 'self' https:;";
    document.head.appendChild(csp);
  }
  
  // X-Frame-Options (safe in development)
  const xFrame = document.createElement('meta');
  xFrame.httpEquiv = 'X-Frame-Options';
  xFrame.content = 'DENY';
  document.head.appendChild(xFrame);
  
  // X-Content-Type-Options (safe in development)
  const xContent = document.createElement('meta');
  xContent.httpEquiv = 'X-Content-Type-Options';
  xContent.content = 'nosniff';
  document.head.appendChild(xContent);
};

/**
 * Disable drag and drop globally
 */
export const disableDragDropGlobally = () => {
  document.addEventListener('dragover', (e) => {
    e.preventDefault();
    return false;
  });
  
  document.addEventListener('drop', (e) => {
    e.preventDefault();
    return false;
  });
};

/**
 * Monitor for suspicious activity
 */
export const monitorSuspiciousActivity = () => {
  let suspiciousActions = 0;
  
  // Track rapid key presses (automation)
  let keyPressCount = 0;
  let keyPressTimer;
  
  document.addEventListener('keydown', () => {
    keyPressCount++;
    clearTimeout(keyPressTimer);
    
    keyPressTimer = setTimeout(() => {
      if (keyPressCount > 50) {
        suspiciousActions++;
        console.error('[SECURITY] Suspicious activity detected');
      }
      keyPressCount = 0;
    }, 1000);
  });
  
  // Track rapid clicks (automation)
  let clickCount = 0;
  let clickTimer;
  
  document.addEventListener('click', () => {
    clickCount++;
    clearTimeout(clickTimer);
    
    clickTimer = setTimeout(() => {
      if (clickCount > 20) {
        suspiciousActions++;
        console.error('[SECURITY] Suspicious activity detected');
      }
      clickCount = 0;
    }, 1000);
  });
  
  return () => suspiciousActions;
};

/**
 * Initialize all security hardening measures
 */
export const initializeSecurityHardening = (businessName = 'Dog Grooming System') => {
  // Clean page title
  sanitizePageTitle(businessName);
  
  // Hide framework info
  hideFrameworkInfo();
  
  // Disable console in production
  disableConsole();
  
  // Prevent DevTools
  preventDevTools();
  
  // Remove HTML comments
  removeHTMLComments();
  
  // Hide source maps
  hideSourceMaps();
  
  // Prevent frame injection
  preventFrameInjection();
  
  // Add security headers
  addSecurityHeaders();
  
  // Disable right-click globally
  disableRightClickGlobally();
  
  // Disable drag/drop globally
  disableDragDropGlobally();
  
  // Monitor suspicious activity
  const getSuspiciousCount = monitorSuspiciousActivity();
  
  return getSuspiciousCount;
};

/**
 * Clean error messages for production
 */
export const sanitizeErrorMessage = (error) => {
  if (process.env.NODE_ENV === 'production') {
    // Return generic message
    return 'An error occurred. Please try again.';
  }
  return error.message;
};

/**
 * Remove development artifacts
 */
export const removeDevelopmentArtifacts = () => {
  // Remove data-testid attributes
  const testIds = document.querySelectorAll('[data-testid]');
  testIds.forEach(el => el.removeAttribute('data-testid'));
  
  // Remove development class names
  const devClasses = document.querySelectorAll('[class*="dev-"], [class*="debug-"], [class*="test-"]');
  devClasses.forEach(el => {
    el.className = el.className.replace(/\b(dev|debug|test)-\S+/g, '');
  });
};

const securityHardeningUtils = {
  sanitizePageTitle,
  hideFrameworkInfo,
  disableConsole,
  preventDevTools,
  removeHTMLComments,
  obfuscateClassNames,
  hideSourceMaps,
  disableRightClickGlobally,
  preventFrameInjection,
  addSecurityHeaders,
  disableDragDropGlobally,
  monitorSuspiciousActivity,
  initializeSecurityHardening,
  sanitizeErrorMessage,
  removeDevelopmentArtifacts
};

export default securityHardeningUtils;
